import React, { useState, useRef } from 'react';
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Cell } from 'recharts';
import { Dialog } from 'primereact/dialog';
import * as saveSvgAsPng from 'save-svg-as-png';
import { Eye, FileText, Image } from 'lucide-react';

const ProjectProgressOverview = ({ data, loading }) => {
  const [showDetails, setShowDetails] = useState(false);
  const chartRef = useRef(null);
  
  const barColors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];
  const shipThemes = {
    'MDL-1': { gradient: { start: '#3b82f6', end: '#1d4ed8' }, textColor: '#1e40af' },
    'MDL-2': { gradient: { start: '#10b981', end: '#047857' }, textColor: '#065f46' },
    'MDL-3': { gradient: { start: '#f59e0b', end: '#d97706' }, textColor: '#92400e' },
    'MDL-4': { gradient: { start: '#8b5cf6', end: '#6d28d9' }, textColor: '#5b21b6' }
  };

  const handleDownloadCSV = () => {
    const dataToDownload = data.map(item => ({
      ship: item.ship,
      currentProgress: item.currentProgress ?? '',
      threeMonthAvg: item.threeMonthAvg ?? '',
      askingRate: item.askingRate ?? '',
      sopLabel: item.sopLabel ?? '',
      rddLabel: item.rddLabel ?? ''
    }));

    const csvContent = [
      ['Ship', 'Current Progress (%)', '3 Month Average (%)', 'Asking Rate (%)', 'SoP', 'RDD'].join(','),
      ...dataToDownload.map(item => [
        item.ship,
        item.currentProgress,
        item.threeMonthAvg,
        item.askingRate,
        item.sopLabel,
        item.rddLabel
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'project-progress-overview.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPNG = () => {
    const chartContainer = chartRef.current?.querySelector('.recharts-wrapper');
    if (chartContainer) {
      saveSvgAsPng.saveSvgAsPng(
        chartContainer.querySelector('svg'),
        'project-progress-overview.png',
        {
          scale: 2,
          backgroundColor: '#ffffff'
        }
      );
    }
  };

  const renderDetailsTable = () => {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ship</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Progress (%)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">3 Month Average (%)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asking Rate (%)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SoP</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">RDD</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item, index) => (
              <tr key={index}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.ship}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.currentProgress ?? '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.threeMonthAvg ?? '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.askingRate ?? '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.sopLabel ?? '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.rddLabel ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">Loading ship progress...</div>;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6" ref={chartRef}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800"></h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowDetails(true)}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center"
          >
            <Eye size={16} className="mr-1" />
            View Details
          </button>
          <button
            onClick={handleDownloadCSV}
            className="text-sm text-gray-600 hover:text-gray-800 font-medium flex items-center"
            title="Download CSV"
          >
            <FileText size={16} className="mr-1" />
            CSV
          </button>
          <button
            onClick={handleDownloadPNG}
            className="text-sm text-gray-600 hover:text-gray-800 font-medium flex items-center"
            title="Download PNG"
          >
            <Image size={16} className="mr-1" />
            PNG
          </button>
        </div>
      </div>

      <Dialog
        visible={showDetails}
        onHide={() => setShowDetails(false)}
        header="Project Progress Detailed Data"
        className="w-full max-w-7xl"
        draggable={false}
      >
        {renderDetailsTable()}
      </Dialog>

      <ResponsiveContainer width="100%" height={320}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 20, right: 60, left: 60, bottom: 20 }}
          barCategoryGap={32}
        >
          <defs>
            {Object.entries(shipThemes).map(([ship, theme]) => (
              <linearGradient key={ship} id={`${ship}Gradient`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={theme.gradient.start} />
                <stop offset="100%" stopColor={theme.gradient.end} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
          <XAxis 
            type="number" 
            domain={[0, 100]} 
            tick={{ fill: '#6b7280', fontWeight: 600, fontSize: 14 }}
            tickFormatter={(value) => `${value}%`}
          />
          <YAxis
            dataKey="ship"
            type="category"
            tick={({ x, y, payload }) => {
              const theme = shipThemes[payload.value] || { textColor: '#374151' };
              return (
                <g transform={`translate(${x},${y})`}>
                  <text
                    x={-10}
                    y={0}
                    dy={4}
                    textAnchor="end"
                    fill={theme.textColor}
                    fontWeight={700}
                    fontSize={16}
                  >
                    {payload.value}
                  </text>
                </g>
              );
            }}
            width={180}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const d = payload[0].payload;
                const theme = shipThemes[d.ship] || { bgColor: '#f3f4f6', borderColor: '#d1d5db', textColor: '#374151' };
                return (
                  <div className="p-4 rounded-lg shadow-lg border" style={{ background: theme.bgColor, borderColor: theme.borderColor }}>
                    <div className="font-bold text-lg mb-2" style={{ color: theme.textColor }}>{d.ship}</div>
                    <div className="space-y-1">
                      <div className="flex justify-between"><span className="font-medium">SoP:</span><span>{d.sopLabel}</span></div>
                      <div className="flex justify-between"><span className="font-medium">RDD:</span><span>{d.rddLabel}</span></div>
                      <div className="flex justify-between"><span className="font-medium">Progress:</span><span>{d.currentProgress}%</span></div>
                      <div className="flex justify-between"><span className="font-medium">3M Avg:</span><span>{d.threeMonthAvg}%</span></div>
                      <div className="flex justify-between"><span className="font-medium">Asking Rate:</span><span>{d.askingRate}%</span></div>
                    </div>
                  </div>
                );
              }
              return null;
            }}
          />
          <Bar
            dataKey="currentProgress"
            barSize={28}
            radius={[8, 8, 8, 8]}
            label={{
              position: 'right',
              fill: '#374151',
              fontWeight: 700,
              fontSize: 16,
              formatter: (value) => `${value}%`,
            }}
          >
            {data.map((entry, idx) => (
              <Cell key={`cell-${idx}`} fill={barColors[idx % barColors.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ProjectProgressOverview;